/**
 * Central auth HTTP + session shaping for FxBazzar mobile.
 * Replaces duplicated axios/fetch/JWT logic in Login.js and TradingAccountsModal.js.
 *
 * - Password login + InstantDemo hit the same REST paths on ENV.LIVE_API.
 * - UI components only handle state; this module handles wire format and parsing.
 */

import axios from "axios";
import { ENV } from "../config/env";
import { errorLog } from "../utils/logger";
import { decodeJwtPayload } from "../utils/jwtDecode";
import { resolveIsMasterRoleFromLoginPayload } from "../utils/sessionRole";
import {
  applyEncryptionHeaders,
  decryptFromEnvelopeJson,
  encryptToEnvelopeJson,
  encryptTokenToBase64Envelope,
  isApiCryptoEnabled,
  looksLikeEnvelope,
} from "../utils/apiCrypto";

/** Session labels stored with the user record (matches previous Login.js constants). */
export const SERVER_LIVE = "FxBazzar-Live";
export const SERVER_DEMO = "FxBazzar-Demo";

/** Backend expects this content-type on user/login and InstantDemo (legacy web parity). */
export const AUTH_JSON_HEADERS = {
  "Content-Type": "application/json-patch+json",
  accept: "*/*",
};

/** True when HttpOnly cookie auth is active (build flag or live session after login). */
export function isAuthCookieSession() {
  if (ENV.USE_AUTH_COOKIES) return true;
  try {
    const raw =
      sessionStorage.getItem("fxbazzar_user") || sessionStorage.getItem("fxbazzar_admin");
    if (raw) return Boolean(JSON.parse(raw).authViaCookies);
  } catch {
    /* ignore */
  }
  return false;
}

const authHttp = axios.create({
  timeout: 15000,
  withCredentials: true,
});

authHttp.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    if (!isApiCryptoEnabled()) return config;

    const method = String(config.method || "get").toUpperCase();
    const hasJsonBodyMethod = method === "POST" || method === "PUT" || method === "PATCH";
    const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

    if (hasJsonBodyMethod && config.data != null && !isFormData) {
      const plain = typeof config.data === "string" ? config.data : JSON.stringify(config.data);
      config.data = JSON.parse(await encryptToEnvelopeJson(plain));
      applyEncryptionHeaders(config.headers, { encryptedBody: true, encryptedResponse: true });
    } else {
      applyEncryptionHeaders(config.headers, { encryptedResponse: true });
    }

    const authHeader = String(config.headers.Authorization || "");
    if (authHeader.startsWith("Bearer ")) {
      const token = authHeader.slice(7).trim();
      if (token && !token.startsWith("eyJ")) {
        applyEncryptionHeaders(config.headers, { encryptedAuth: true });
      } else if (token) {
        const encToken = await encryptTokenToBase64Envelope(token);
        config.headers.Authorization = `Bearer ${encToken}`;
        applyEncryptionHeaders(config.headers, { encryptedAuth: true });
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

authHttp.interceptors.response.use(
  async (response) => {
    if (isApiCryptoEnabled() && looksLikeEnvelope(response?.data)) {
      const plain = await decryptFromEnvelopeJson(response.data);
      response.data = JSON.parse(plain);
    }
    return response;
  },
  (error) => Promise.reject(error)
);

function trimBase(value) {
  return String(value || "").replace(/\/+$/, "");
}

export function getAuthApiBase() {
  const apiBase = trimBase(process.env.REACT_APP_API_BASE);
  if (apiBase) return apiBase;
  // Development: same-origin so login Set-Cookie is stored for :3000 (CRA proxy).
  if (process.env.NODE_ENV === "development") return "";
  if (isAuthCookieSession() || ENV.USE_AUTH_COOKIES) return "";
  return trimBase(ENV.LIVE_API);
}

function getAuthBaseCandidates() {
  // Cookie login must not fall back to https://localhost:44319 — that would set
  // fxb_access on the API host while History still calls the CRA origin.
  if (process.env.NODE_ENV === "development" && !trimBase(process.env.REACT_APP_API_BASE)) {
    return [""];
  }
  const primary = getAuthApiBase();
  return [...new Set([primary, trimBase(ENV.LIVE_API)].filter(Boolean))];
}

function joinAuthUrl(base, path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${trimBase(base)}${p}`;
}

async function postAuthJson(path, body) {
  let lastError;
  for (const base of getAuthBaseCandidates()) {
    try {
      const { data } = await authHttp.post(joinAuthUrl(base, path), body, {
        headers: AUTH_JSON_HEADERS,
        withCredentials: true,
      });
      return data;
    } catch (err) {
      lastError = err;
      if (err?.response && err.response.status !== 502 && err.response.status !== 504) {
        throw err;
      }
    }
  }
  throw lastError;
}

/**
 * Best-effort public IP for login payload (same fallback chain as before).
 */
export async function fetchClientIp({ timeoutMs = 4000, fallback = "127.0.0.1" } = {}) {
  const services = [
    "https://api.ipify.org?format=json",
    "https://api.my-ip.io/ip.json",
    "https://ipapi.co/json/",
  ];
  for (const url of services) {
    try {
      const r = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
      const obj = await r.json();
      const ip = obj.ip || obj.IP;
      if (ip) return ip;
    } catch {
      continue;
    }
  }
  return fallback;
}

export function buildPasswordLoginBody({
  username,
  password,
  userIp,
  isUserLogin,
}) {
  return {
    username: String(username || "").trim(),
    password,
    deviceType: 1,
    sourceDevice: "web",
    userIp,
    isUserLogin: Boolean(isUserLogin),
    projectType: 1,
    totp: 0,
    fcmToken: "",
  };
}

/** Shared InstantDemo body (username/password placeholders per API contract). */
export function buildInstantDemoBody({ userIp, isUserLogin = true }) {
  return {
    username: "abc",
    password: "abc",
    deviceType: 1,
    sourceDevice: "web",
    userIp,
    isUserLogin,
    projectType: 1,
    totp: 0,
    fcmToken: "",
  };
}

export function mapAxiosErrorToMessage(err) {
  if (err?.response) {
    return err.response.data?.message || err.response.statusText || "Request failed";
  }
  if (err?.code === "ECONNABORTED") {
    return "Login timed out. Keep FastTrade.API running (F5) and do not leave the debugger paused.";
  }
  if (err?.request) {
    return "Cannot reach FastTrade.API through the dev proxy. Start the API in Visual Studio (IIS Express on https://localhost:44319) and keep npm start running.";
  }
  return err?.message || "Unknown error";
}

function assertLoginResponseOk(data) {
  if (data?.status !== 200) {
    throw new Error(data?.message || "Login failed");
  }
  const apiData = data.data || {};
  const tokens = apiData.tokens || apiData.Tokens || {};
  const token =
    tokens.accessToken ||
    tokens.AccessToken ||
    apiData.accessToken ||
    apiData.AccessToken ||
    apiData.token ||
    apiData.Token ||
    "";

  const hasUserIdentity = Boolean(
    apiData.userId ??
      apiData.UserId ??
      apiData.userName ??
      apiData.UserName ??
      apiData.userCode ??
      apiData.UserCode
  );

  const accessInPayload =
    tokens.accessToken ?? tokens.AccessToken ?? apiData.accessToken ?? apiData.AccessToken;
  const refreshInPayload =
    tokens.refreshToken ?? tokens.RefreshToken ?? apiData.refreshToken ?? apiData.RefreshToken;

  // AuthCookies + EmitTokensInJson:false: login succeeds with user fields, JWT only in HttpOnly cookies.
  const emptyTokensFromCookieMode =
    !token &&
    hasUserIdentity &&
    (accessInPayload === undefined ||
      accessInPayload === null ||
      String(accessInPayload) === "") &&
    (refreshInPayload === undefined ||
      refreshInPayload === null ||
      String(refreshInPayload) === "");

  const useCookieAuth = ENV.USE_AUTH_COOKIES || emptyTokensFromCookieMode;

  if (!token && !useCookieAuth) {
    throw new Error("No access token received.");
  }

  const jwt = token ? decodeJwtPayload(token) : {};
  return { apiData, token, jwt, useCookieAuth };
}

function parseUserId(jwt, apiData) {
  return parseInt(
    jwt.Id ||
      jwt.id ||
      jwt.userId ||
      jwt.UserId ||
      apiData.userId ||
      apiData.UserId ||
      apiData.user_id ||
      0,
    10
  );
}

/**
 * POST /api/v1/user/login — returns raw API body `{ status, data, message }`.
 */
export async function postUserLogin(body) {
  return postAuthJson("/api/v1/user/login", body);
}

/**
 * POST /api/v1/user/InstantDemo
 */
export async function postInstantDemo(body) {
  return postAuthJson("/api/v1/user/InstantDemo", body);
}

let refreshAuthCookiesPromise = null;

/**
 * One shared refresh for concurrent 401s. Uses HttpOnly fxb_access + fxb_refresh cookies (no JSON body).
 * Backend issues new cookies; optional JWT in JSON depends on AuthCookies:EmitTokensInJson.
 */
export function refreshAuthCookiesOnce() {
  if (!isAuthCookieSession()) return Promise.resolve(false);
  if (refreshAuthCookiesPromise) return refreshAuthCookiesPromise;

  const base = getAuthApiBase();
  const runRefresh = (retryStaleAccess) =>
    authHttp
      .post(`${base}/api/v1/user/refresh-token`, {}, { headers: AUTH_JSON_HEADERS })
      .then((res) => {
        const ok = res?.data?.status === 200;
        if (!ok) {
          const msg = String(res?.data?.message || "");
          if (
            !retryStaleAccess &&
            msg.toLowerCase().includes("token doesn't match the saved token")
          ) {
            return runRefresh(true);
          }
          return false;
        }
        const tokens = res?.data?.data?.tokens || res?.data?.data?.Tokens || {};
        const access =
          tokens.accessToken || tokens.AccessToken || res?.data?.data?.accessToken || "";
        if (access) {
          try {
            for (const key of ["fxbazzar_user", "fxbazzar_admin"]) {
              const raw = sessionStorage.getItem(key);
              if (!raw) continue;
              const d = JSON.parse(raw);
              d.token = access;
              d.Token = access;
              d.authViaCookies = false;
              sessionStorage.setItem(key, JSON.stringify(d));
            }
          } catch {
            /* ignore */
          }
        }
        return true;
      })
      .catch((err) => {
        const msg = String(err?.response?.data?.message || "");
        if (
          !retryStaleAccess &&
          msg.toLowerCase().includes("token doesn't match the saved token")
        ) {
          return runRefresh(true);
        }
        return false;
      });

  refreshAuthCookiesPromise = runRefresh(false).finally(() => {
    refreshAuthCookiesPromise = null;
  });

  return refreshAuthCookiesPromise;
}

/** Display login string for InstantDemo success (modal uses richer label than main Login). */
export function resolveInstantDemoDisplayLogin(apiResponseData) {
  const d = apiResponseData?.data || {};
  return String(d.UserCode || d.UserName || d.userName || "demo").trim() || "demo";
}

/**
 * Full session object for sessionStorage + App `onLogin` (Login screen).
 */
export function buildAppSessionFromLoginResponse(
  data,
  {
    displayLogin,
    isDemoAccount,
    isAdminLogin,
    userIpForSession = "",
  }
) {
  const { apiData, token, jwt, useCookieAuth } = assertLoginResponseOk(data);
  const userId = parseUserId(jwt, apiData);
  const userName =
    jwt.UserName ||
    jwt.userName ||
    apiData.UserCode ||
    apiData.UserName ||
    apiData.userName ||
    displayLogin ||
    "User";

  if (userId === 0) errorLog("userId missing in login response (check UserId in API or cookies)");

  const role = jwt.Role || apiData.role || apiData.Role || "User";
  const roles = apiData.roles || apiData.Roles || [role];
  const positionDataTypeRaw =
    apiData.positionDataType ??
    apiData.PositionDataType ??
    apiData?.mobileInformation?.positionDataType ??
    apiData?.mobileInformation?.PositionDataType ??
    apiData?.MobileInformation?.positionDataType ??
    apiData?.MobileInformation?.PositionDataType;
  const positionDataType =
    Number(positionDataTypeRaw) === 1 || String(positionDataTypeRaw).toLowerCase() === "ltpwise"
      ? 1
      : 2;

  const ip =
    userIpForSession ||
    apiData.ip ||
    apiData.IP ||
    apiData.userIp ||
    apiData.UserIp ||
    "";

  return {
    userId,
    userName,
    role,
    roles,
    positionDataType,
    PositionDataType: positionDataType,
    mobileInformation: {
      positionDataType,
      PositionDataType: positionDataType,
    },
    token,
    authViaCookies: useCookieAuth,
    isAdminLogin,
    isDemoAccount: Boolean(isDemoAccount),
    server: isDemoAccount ? SERVER_DEMO : SERVER_LIVE,
    isMasterRole: isDemoAccount
      ? true
      : resolveIsMasterRoleFromLoginPayload(apiData, jwt),
    userIpFinal: ip,
    userIp: ip,
  };
}

/**
 * Slim connected-account shape for TradingAccountsModal list/detail.
 */
export function buildTradingAccountFromLoginResponse(data, displayLogin, isDemoAccount) {
  const { apiData, token, jwt, useCookieAuth } = assertLoginResponseOk(data);
  const userId = parseUserId(jwt, apiData);
  const jwtName =
    jwt.UserName || apiData.UserCode || apiData.UserName || displayLogin || "";
  const name =
    jwtName ||
    apiData.userName ||
    apiData.FullName ||
    apiData.fullName ||
    apiData.Name ||
    apiData.name ||
    "";

  if (userId === 0) errorLog("userId missing in JWT (TradingAccountsModal)");

  return {
    login: displayLogin,
    name: name || displayLogin || "User",
    server: isDemoAccount ? SERVER_DEMO : SERVER_LIVE,
    token,
    authViaCookies: useCookieAuth,
    userId,
    balance:
      apiData.Balance ||
      apiData.balance ||
      apiData.AccountBalance ||
      apiData.accountBalance ||
      apiData.WalletBalance ||
      apiData.walletBalance ||
      null,
    currency: apiData.Currency || apiData.currency || "USD",
    userCode:
      apiData.UserCode ||
      apiData.userCode ||
      apiData.UserId ||
      apiData.userId ||
      "",
    isDemo: Boolean(isDemoAccount),
    isMasterRole: isDemoAccount
      ? true
      : resolveIsMasterRoleFromLoginPayload(apiData, jwt),
  };
}

/**
 * POST /api/v1/user/reset-password (Bearer token).
 */
export async function postResetPassword(token, payload) {
  const base = getAuthApiBase();
  const { data } = await authHttp.post(`${base}/api/v1/user/reset-password`, payload, {
    headers: {
      ...AUTH_JSON_HEADERS,
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
}
